import { useState, useEffect } from "react";
import { Film, Globe, Calendar, ExternalLink, Search, Plus, CheckCircle2, XCircle, Edit, Trash2, X, Sparkles, Clock, Send } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { FestivalSubmissionModal } from "@/components/FestivalSubmissionModal";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Festivals() {
  const { user } = useAuth();
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("all");
  const [country, setCountry] = useState("all");
  const [month, setMonth] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Admin Modal
  const [showModal, setShowModal] = useState(false);
  const [editingFest, setEditingFest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    short_name: "",
    ciudad: "",
    pais: "",
    region: "LatAm",
    mes: "Junio",
    tipo: "Largometrajes, Cortometrajes, Documentales",
    estado: "Convocatoria Abierta",
    enlace_oficial: "",
    logo_url: "",
    banner_url: "",
    descripcion: ""
  });

  useEffect(() => {
    fetchFestivals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, country, month, typeFilter, statusFilter]);

  const fetchFestivals = async () => {
    setLoading(true);
    try {
      const params = {};
      if (region !== "all") params.region = region;
      if (country !== "all") params.country = country;
      if (month !== "all") params.month = month;
      if (typeFilter !== "all") params.tipo = typeFilter;
      if (statusFilter !== "all") params.estado = statusFilter;

      const { data } = await api.get("/festivals", { params });
      setFestivals(data);
    } catch (err) {
      console.error("Error fetching festivals:", err);
      toast.error("Error al cargar la lista de festivales");
    } finally {
      setLoading(false);
    }
  };

  const filteredFestivals = festivals.filter(f => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = f.nombre || f.name || "";
    const city = f.ciudad || f.city || "";
    const pais = f.pais || f.country || "";
    const desc = f.descripcion || f.description || "";
    return (
      name.toLowerCase().includes(q) ||
      (f.short_name || "").toLowerCase().includes(q) ||
      city.toLowerCase().includes(q) ||
      pais.toLowerCase().includes(q) ||
      desc.toLowerCase().includes(q)
    );
  });

  const uniqueCountries = Array.from(new Set(festivals.map(f => f.pais || f.country).filter(Boolean))).sort();

  const handleOpenModal = (fest = null) => {
    if (fest) {
      setEditingFest(fest);
      setFormData({
        nombre: fest.nombre || fest.name || "",
        short_name: fest.short_name || "",
        ciudad: fest.ciudad || fest.city || "",
        pais: fest.pais || fest.country || "",
        region: fest.region || "LatAm",
        mes: fest.mes || fest.month || "Junio",
        tipo: Array.isArray(fest.tipo || fest.category_types)
          ? (fest.tipo || fest.category_types).join(", ")
          : (fest.tipo || fest.category_types || ""),
        estado: fest.estado || (fest.call_open ? "Convocatoria Abierta" : "Cerrado"),
        enlace_oficial: fest.enlace_oficial || fest.website_url || "",
        logo_url: fest.logo_url || "",
        banner_url: fest.banner_url || fest.image_url || "",
        descripcion: fest.descripcion || fest.description || ""
      });
    } else {
      setEditingFest(null);
      setFormData({
        nombre: "",
        short_name: "",
        ciudad: "",
        pais: "",
        region: "LatAm",
        mes: "Junio",
        tipo: "Largometrajes, Cortometrajes, Documentales",
        estado: "Convocatoria Abierta",
        enlace_oficial: "",
        logo_url: "",
        banner_url: "",
        descripcion: ""
      });
    }
    setShowModal(true);
  };

  const handleSaveFestival = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        tipo: formData.tipo.split(",").map(s => s.trim()).filter(Boolean)
      };

      if (editingFest) {
        await api.put(`/admin/festivals/${editingFest.id}`, payload);
        toast.success("Festival actualizado con éxito");
      } else {
        await api.post("/admin/festivals", payload);
        toast.success("Nuevo festival creado correctamente");
      }

      setShowModal(false);
      fetchFestivals();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error al guardar festival");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFestival = async (id, name) => {
    if (!window.confirm(`¿Estás seguro de eliminar el festival "${name}"?`)) return;
    try {
      await api.delete(`/admin/festivals/${id}`);
      toast.success("Festival eliminado");
      fetchFestivals();
    } catch (err) {
      toast.error("Error al eliminar festival");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setRegion("all");
    setCountry("all");
    setMonth("all");
    setTypeFilter("all");
    setStatusFilter("all");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-16">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Banner Hero */}
        <div className="relative rounded-lg overflow-hidden bg-gradient-to-r from-[#E11D48]/20 via-zinc-900 to-zinc-950 border border-white/10 p-8 sm:p-12 mb-10 shadow-2xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-[#E11D48] text-white text-xs font-bold uppercase tracking-wider mb-4 shadow-md">
              <Film size={14} /> Guía Mundial de Festivales LGBTQ+
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-4 leading-tight">
              Festivales de Cine <span className="prism-text">LGBTQ+</span>
            </h1>
            <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-6">
              Descubre y filtra eventos cinematográficos queer de todo el planeta: desde cortometrajes independientes hasta grandes certámenes clasificatorios. Postula tu festival para incluirlo en nuestro directorio oficial.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowSubmitModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E11D48] hover:bg-[#BE123C] text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-lg hover:scale-[1.02]"
              >
                <Send size={16} /> Postular mi Festival / Solicitar Inclusión
              </button>

              {user?.role === "admin" && (
                <button
                  onClick={() => handleOpenModal()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs sm:text-sm font-bold rounded-xl border border-white/10 transition-all shadow-lg"
                >
                  <Plus size={16} /> Crear Directamente (Admin)
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="bg-zinc-900/90 border border-white/10 rounded-lg p-5 mb-8 shadow-xl">
          <div className="flex items-center justify-between gap-4 mb-4 pb-3 border-b border-white/10">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <Sparkles size={16} className="text-[#E11D48]" /> Filtros de Búsqueda
            </h2>
            <button
              onClick={resetFilters}
              className="text-xs text-zinc-400 hover:text-white transition-colors"
            >
              Limpiar filtros
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Search Input */}
            <div className="lg:col-span-2 relative flex items-center">
              <Search size={16} className="absolute left-3 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nombre, ciudad o palabra clave..."
                className="w-full bg-zinc-950 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
              />
            </div>

            {/* Region Select */}
            <div>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-200 focus:outline-none focus:border-[#E11D48]"
              >
                <option value="all">Todas las Regiones</option>
                <option value="LatAm">América Latina (LatAm)</option>
                <option value="Europa">Europa</option>
                <option value="Norteamérica">Norteamérica</option>
                <option value="Asia/Oceanía">Asia / Oceanía</option>
              </select>
            </div>

            {/* Country Select */}
            <div>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-200 focus:outline-none focus:border-[#E11D48]"
              >
                <option value="all">Todos los Países</option>
                {uniqueCountries.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Month Select */}
            <div>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-200 focus:outline-none focus:border-[#E11D48]"
              >
                <option value="all">Cualquier Mes</option>
                {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Status Select */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-200 focus:outline-none focus:border-[#E11D48]"
              >
                <option value="all">Todos los Estados</option>
                <option value="Convocatoria Abierta">Convocatoria Abierta</option>
                <option value="Próxima Edición">Próxima Edición</option>
                <option value="Cerrado">Cerrado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-xs text-zinc-400">
            Mostrando <span className="text-white font-bold">{filteredFestivals.length}</span> festivales de cine LGBTQ+
          </p>
        </div>

        {/* Festivals Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(n => (
              <div key={n} className="h-72 bg-zinc-900/50 rounded-lg animate-pulse border border-white/5" />
            ))}
          </div>
        ) : filteredFestivals.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900/40 rounded-lg border border-white/10">
            <Film size={44} className="mx-auto text-zinc-600 mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">No hay festivales que coincidan</h3>
            <p className="text-zinc-400 text-xs max-w-md mx-auto mb-4">
              Intenta cambiar los parámetros de búsqueda o limpiar los filtros seleccionados.
            </p>
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-[#E11D48] text-white text-xs font-bold rounded-lg hover:bg-[#BE123C]"
            >
              Ver todos los festivales
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFestivals.map(fest => {
              const name = fest.nombre || fest.name;
              const city = fest.ciudad || fest.city;
              const countryName = fest.pais || fest.country;
              const monthName = fest.mes || fest.month;
              const types = fest.tipo || fest.category_types || [];
              const status = fest.estado || (fest.call_open ? "Convocatoria Abierta" : "Cerrado");
              const banner = fest.banner_url || fest.image_url;
              const website = fest.enlace_oficial || fest.website_url;
              const desc = fest.descripcion || fest.description;

              return (
                <div
                  key={fest.id}
                  className="group bg-zinc-900/80 hover:bg-zinc-900 border border-white/10 hover:border-[#E11D48]/50 rounded-lg overflow-hidden transition-all duration-300 flex flex-col justify-between shadow-xl"
                >
                  <div>
                    {/* Poster Image */}
                    <div className="relative h-48 overflow-hidden bg-zinc-950">
                      <img
                        src={banner}
                        alt={name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />

                      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                        <span className="bg-black/75 backdrop-blur-md border border-white/20 text-white text-[11px] font-semibold px-2.5 py-1 rounded-lg flex items-center gap-1">
                          <Globe size={12} className="text-[#E11D48]" /> {city}, {countryName}
                        </span>
                      </div>

                      <div className="absolute top-3 right-3">
                        {status === "Convocatoria Abierta" ? (
                          <span className="bg-emerald-500/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-md">
                            <CheckCircle2 size={11} /> Convocatoria Abierta
                          </span>
                        ) : status === "Próxima Edición" ? (
                          <span className="bg-amber-500/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-md">
                            <Clock size={11} /> Próxima Edición
                          </span>
                        ) : (
                          <span className="bg-zinc-800/90 text-zinc-300 text-[10px] font-medium px-2 py-1 rounded-lg flex items-center gap-1">
                            <XCircle size={11} /> Convocatoria Cerrada
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Body Content */}
                    <div className="p-5">
                      <div className="flex items-center justify-between text-xs text-amber-400 font-bold mb-1.5">
                        <span><Calendar size={12} className="inline mr-1" />{monthName} • {fest.region}</span>
                      </div>

                      <h3 className="text-lg font-extrabold text-white group-hover:text-[#E11D48] transition-colors leading-snug mb-2">
                        {name}
                      </h3>

                      <p className="text-xs text-zinc-400 line-clamp-3 leading-relaxed mb-4">
                        {desc}
                      </p>

                      {/* Category Tags */}
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {types.map((cat, idx) => (
                          <span key={idx} className="bg-zinc-800/90 text-zinc-300 text-[10px] font-medium px-2.5 py-1 rounded-lg border border-white/5">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Footer Controls */}
                  <div className="px-5 pb-4 pt-3 border-t border-white/5 flex items-center justify-between bg-zinc-950/40">
                    <div className="flex items-center gap-2">
                      {user?.role === "admin" && (
                        <>
                          <button
                            onClick={() => handleOpenModal(fest)}
                            className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                            title="Editar Festival"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteFestival(fest.id, name)}
                            className="p-1.5 text-zinc-400 hover:text-rose-400 hover:bg-zinc-800 rounded-lg transition-colors"
                            title="Eliminar Festival"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>

                    {website && (
                      <a
                        href={website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-[#E11D48] hover:text-rose-400 font-bold transition-colors"
                      >
                        Sitio Oficial <ExternalLink size={13} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Admin Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-white/10 rounded-lg w-full max-w-2xl p-6 relative shadow-2xl my-8">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white p-1 rounded-lg"
            >
              <X size={20} />
            </button>

            <h3 className="text-xl font-bold text-white mb-6">
              {editingFest ? "Editar Festival" : "Nuevo Festival de Cine LGBTQ+"}
            </h3>

            <form onSubmit={handleSaveFestival} className="space-y-4 text-xs">
              <div>
                <label className="block text-zinc-300 font-semibold mb-1">Nombre Completo del Festival *</label>
                <input
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: LesGaiCineMad — Festival Internacional de Cine LGBTIQ+ de Madrid"
                  className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Nombre Corto / Acrónimo</label>
                  <input
                    value={formData.short_name}
                    onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                    placeholder="Ej: LesGaiCineMad"
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Región *</label>
                  <select
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  >
                    <option value="LatAm">América Latina (LatAm)</option>
                    <option value="Europa">Europa</option>
                    <option value="Norteamérica">Norteamérica</option>
                    <option value="Asia/Oceanía">Asia / Oceanía</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Ciudad *</label>
                  <input
                    required
                    value={formData.ciudad}
                    onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                    placeholder="Ej: Madrid"
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">País *</label>
                  <input
                    required
                    value={formData.pais}
                    onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                    placeholder="Ej: España"
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Mes de Celebración *</label>
                  <select
                    value={formData.mes}
                    onChange={(e) => setFormData({ ...formData, mes: e.target.value })}
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  >
                    {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Estado *</label>
                  <select
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  >
                    <option value="Convocatoria Abierta">Convocatoria Abierta</option>
                    <option value="Próxima Edición">Próxima Edición</option>
                    <option value="Cerrado">Cerrado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">Sitio Web Oficial</label>
                  <input
                    value={formData.enlace_oficial}
                    onChange={(e) => setFormData({ ...formData, enlace_oficial: e.target.value })}
                    placeholder="https://..."
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-300 font-semibold mb-1">Tipos / Categorías (separados por coma)</label>
                <input
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  placeholder="Largometrajes, Cortometrajes, Documentales, Trans/Queer, Animación"
                  className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">URL Logo</label>
                  <input
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 font-semibold mb-1">URL Banner / Cartel</label>
                  <input
                    value={formData.banner_url}
                    onChange={(e) => setFormData({ ...formData, banner_url: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-300 font-semibold mb-1">Descripción / Misión</label>
                <textarea
                  rows={3}
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Resumen del festival, trayectoria e importancia..."
                  className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-[#E11D48]"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-medium rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-[#E11D48] hover:bg-[#BE123C] text-white font-bold rounded-lg transition-colors"
                >
                  {saving ? "Guardando..." : "Guardar Festival"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Festival Public Submission Modal */}
      <FestivalSubmissionModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
      />
    </div>
  );
}
